import { useEffect, useMemo, useState } from 'react';
import AdminDocActions from './AdminDocActions';
import DocumentViewer from './DocumentViewer';
import { EmptyState, Notice, StatusPill, Tabs } from './ui';
import { DOC_CATEGORY, DOC_STATUS, formatBytes, formatDateTime } from '../lib/format';
import { CARD, CARD_STYLE } from '../lib/ui';
import type { CompanyDocument, Requirement } from '../types/workspace';

const CATEGORY_ORDER = ['FINANCIAL', 'LEGAL', 'KYC', 'BUSINESS', 'OTHER'];

type Row = { r: Requirement; d?: CompanyDocument };
type CatFilter = 'ALL' | string;
type StatusFilter = 'all' | 'uploaded' | 'attention' | 'verified' | 'rejected' | 'missing';

type Props = {
  companyId: string;
  requirements: Requirement[];
  documents: CompanyDocument[];
  missing: Requirement[];
  onChanged: () => void;
  onError: (m: string) => void;
};

function statusMatch(filter: StatusFilter, row: Row): boolean {
  const s = row.d?.status;
  switch (filter) {
    case 'uploaded':
      return !!row.d;
    case 'attention':
      return s === 'NEEDS_REVIEW' || s === 'UPLOADED' || s === 'VERIFYING';
    case 'verified':
      return s === 'VERIFIED';
    case 'rejected':
      return s === 'REJECTED';
    case 'missing':
      return !row.d && row.r.required;
    default:
      return true;
  }
}

export default function DocumentsWorkspace({
  companyId,
  requirements,
  documents,
  missing,
  onChanged,
  onError,
}: Props) {
  const [cat, setCat] = useState<CatFilter>('ALL');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  const rows = useMemo(() => {
    const docByCode = new Map(documents.map((d) => [d.code, d]));
    const extras = documents.filter((d) => !requirements.some((r) => r.code === d.code));
    return [
      ...requirements.map((r) => ({ r, d: docByCode.get(r.code) })),
      ...extras.map((d) => ({
        r: {
          id: null,
          code: d.code,
          title: d.title,
          description: 'Supporting document',
          required: false,
          category: null,
          tracks: [],
        } as Requirement,
        d,
      })),
    ];
  }, [documents, requirements]);

  const groups = useMemo(
    () =>
      CATEGORY_ORDER.map((c) => ({
        cat: c,
        rows: rows.filter(({ r }) => (r.category || 'OTHER') === c),
      })).filter((g) => g.rows.length),
    [rows],
  );

  const filtered = useMemo(() => {
    const byCat = cat === 'ALL' ? rows : rows.filter(({ r }) => (r.category || 'OTHER') === cat);
    return byCat.filter((row) => statusMatch(status, row));
  }, [rows, cat, status]);

  useEffect(() => {
    if (selectedCode && filtered.some((x) => x.r.code === selectedCode)) return;
    const firstUploaded = filtered.find((x) => x.d) || filtered[0];
    setSelectedCode(firstUploaded?.r.code || null);
  }, [filtered, selectedCode]);

  const selected = filtered.find((x) => x.r.code === selectedCode) || null;
  const selectedDoc = selected?.d;
  const fileUrl = selectedDoc ? `/admin/companies/${companyId}/documents/${selectedDoc.id}/file` : null;

  const counts = useMemo(
    () => ({
      all: rows.length,
      uploaded: rows.filter((x) => x.d).length,
      attention: rows.filter((x) => statusMatch('attention', x)).length,
      verified: rows.filter((x) => statusMatch('verified', x)).length,
      rejected: rows.filter((x) => statusMatch('rejected', x)).length,
      missing: missing.length,
    }),
    [rows, missing.length],
  );

  return (
    <div className="space-y-4">
      {missing.length > 0 && (
        <Notice tone="amber">
          Missing required ({missing.length}): {missing.map((m) => m.title).join(', ')}
        </Notice>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setCat('ALL')}
          className={`rounded-xl px-3 py-1.5 text-xs font-bold ${cat === 'ALL' ? 'text-white' : 'text-[#6b0030]'}`}
          style={
            cat === 'ALL'
              ? { background: '#d6006e' }
              : { background: '#fff', border: '1px solid #fce7f3' }
          }
        >
          All categories · {counts.all}
        </button>
        {groups.map((g) => (
          <button
            key={g.cat}
            type="button"
            onClick={() => setCat(g.cat)}
            className={`rounded-xl px-3 py-1.5 text-xs font-bold ${cat === g.cat ? 'text-white' : 'text-[#6b0030]'}`}
            style={
              cat === g.cat
                ? { background: '#d6006e' }
                : { background: '#fff', border: '1px solid #fce7f3' }
            }
          >
            {DOC_CATEGORY[g.cat]?.label || g.cat} · {g.rows.filter((x) => x.d).length}/{g.rows.length}
          </button>
        ))}
      </div>

      <Tabs
        value={status}
        onChange={setStatus}
        options={[
          { id: 'all', label: 'All', count: counts.all },
          { id: 'uploaded', label: 'Uploaded', count: counts.uploaded },
          { id: 'attention', label: 'Needs attention', count: counts.attention },
          { id: 'verified', label: 'Approved', count: counts.verified },
          { id: 'rejected', label: 'Rejected', count: counts.rejected },
          { id: 'missing', label: 'Missing', count: counts.missing },
        ]}
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)] xl:items-start">
        <div className={`${CARD} p-2`} style={CARD_STYLE}>
          {filtered.length === 0 ? (
            <EmptyState title="No documents in this view" body="Try another category or status filter." />
          ) : (
            <ul className="space-y-1">
              {filtered.map(({ r, d }) => {
                const active = r.code === selectedCode;
                const s = d ? DOC_STATUS[d.status] : null;
                return (
                  <li key={r.code}>
                    <button
                      type="button"
                      onClick={() => setSelectedCode(r.code)}
                      className={`w-full rounded-xl px-3 py-2.5 text-left transition ${
                        active ? 'bg-[#fff0f6] ring-1 ring-[#f9a8d4]' : 'hover:bg-[#fffafc]'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-[#6b0030]">
                            {r.title}
                            {!r.required && <span className="ml-1 text-[10px] font-normal text-[#c084a0]">optional</span>}
                          </p>
                          <p className={`truncate text-[11px] ${d ? 'text-[#c084a0]' : r.required ? 'text-[#be123c]' : 'text-[#c084a0]'}`}>
                            {d ? d.fileName : 'Not uploaded'}
                          </p>
                        </div>
                        {s ? (
                          <StatusPill label={s.label} tone={s.tone} pulse={d?.status === 'VERIFYING'} />
                        ) : (
                          <StatusPill label={r.required ? 'Missing' : 'Empty'} tone={r.required ? 'rose' : 'slate'} />
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="space-y-3">
          {!selected && <EmptyState title="Select a document" body="Pick a row on the left to review the file and approve or reject." />}
          {selected && (
            <>
              <article className={`${CARD} p-4`} style={CARD_STYLE}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">
                      {DOC_CATEGORY[selected.r.category || 'OTHER']?.label || 'Other'}
                    </p>
                    <h3 className="text-base font-black text-[#6b0030]">{selected.r.title}</h3>
                    {selectedDoc ? (
                      <p className="mt-0.5 text-[11px] text-[#c084a0]">
                        {selectedDoc.fileName} · {formatBytes(selectedDoc.sizeBytes)} · {formatDateTime(selectedDoc.createdAt)}
                      </p>
                    ) : (
                      <p className={`mt-0.5 text-[11px] ${selected.r.required ? 'text-[#be123c]' : 'text-[#c084a0]'}`}>
                        Not uploaded yet
                      </p>
                    )}
                  </div>
                  {selectedDoc && DOC_STATUS[selectedDoc.status] && (
                    <StatusPill
                      label={DOC_STATUS[selectedDoc.status].label}
                      tone={DOC_STATUS[selectedDoc.status].tone}
                      pulse={selectedDoc.status === 'VERIFYING'}
                    />
                  )}
                </div>

                {selected.r.description && <p className="mt-2 text-sm text-[#6b3a52]">{selected.r.description}</p>}

                {selectedDoc?.aiSummary && (
                  <p className="mt-3 text-sm text-[#4a1a30]">
                    <span className="font-bold text-[#9d0050]">
                      AI{selectedDoc.aiConfidence != null ? ` · ${selectedDoc.aiConfidence}%` : ''}:{' '}
                    </span>
                    {selectedDoc.aiSummary}
                  </p>
                )}
                {selectedDoc?.aiIssues && selectedDoc.aiIssues.length > 0 && (
                  <ul className="mt-1">
                    {selectedDoc.aiIssues.map((i, n) => (
                      <li key={n} className="text-xs text-[#be123c]">
                        • {i}
                      </li>
                    ))}
                  </ul>
                )}
                {selectedDoc?.reviewNote && (
                  <p className="mt-1 text-xs text-[#6b3a52]">Review note: {selectedDoc.reviewNote}</p>
                )}

                {selectedDoc && (
                  <div className="mt-3">
                    <AdminDocActions
                      companyId={companyId}
                      doc={selectedDoc}
                      onChanged={onChanged}
                      onError={onError}
                      hideView
                    />
                  </div>
                )}
              </article>

              {selectedDoc && fileUrl ? (
                <DocumentViewer fileUrl={fileUrl} fileName={selectedDoc.fileName} mimeType={selectedDoc.mimeType} />
              ) : (
                <div className={`${CARD} flex min-h-[16rem] items-center justify-center p-6`} style={CARD_STYLE}>
                  <p className="text-sm text-[#c084a0]">Upload a file to preview it here.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
